import { NavLink, Navigate, Route, Routes, Link } from "react-router-dom";
import { BarChart3, ClipboardList, Map, Mail, Printer, Settings, Shirt, Tag, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useSeo } from "../../lib/seo";
import { Logo } from "../../components/layout/Logo";
import AdminOverview from "./AdminOverview";
import AdminOrders from "./AdminOrders";
import AdminProducts from "./AdminProducts";
import AdminDestinations from "./AdminDestinations";
import AdminDiscounts from "./AdminDiscounts";
import AdminSettings from "./AdminSettings";
import AdminEmails from "./AdminEmails";
import AdminPrintify from "./AdminPrintify";

const NAV = [
  ["", "Overview", BarChart3],
  ["orders", "Orders", ClipboardList],
  ["products", "Products", Shirt],
  ["destinations", "Destinations", Map],
  ["discounts", "Discounts", Tag],
  ["printify", "Printify", Printer],
  ["emails", "Emails", Mail],
  ["settings", "Marketing & Settings", Settings],
];

export default function Admin() {
  const { user } = useAuth();
  useSeo({ title: "Admin", noindex: true });
  if (user === null) return <div className="min-h-screen bg-sand" />;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin")
    return (
      <div className="min-h-screen grid place-items-center text-center p-6" data-testid="admin-forbidden">
        <div><h1 className="text-3xl font-extrabold text-navy">Admins only</h1><Link to="/" className="mt-4 inline-block underline">Back to store</Link></div>
      </div>
    );
  return (
    <div className="min-h-screen bg-[#F6F8FB] lg:grid lg:grid-cols-[250px_1fr]" data-testid="admin-dashboard">
      <aside className="bg-navy text-white lg:min-h-screen p-4 lg:p-6 lg:sticky lg:top-0 lg:h-screen">
        <div className="flex items-center gap-3">
          <Logo alt="Vacation Apparel" className="h-12" />
          <span className="font-brush text-2xl text-sun">Admin</span>
        </div>
        <nav className="mt-6 flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0 [mask-image:linear-gradient(90deg,#000_85%,transparent)] lg:[mask-image:none]">
          {NAV.map(([to, label, Icon]) => (
            <NavLink key={label} end to={`/admin/${to}`} data-testid={`admin-nav-${label.split(" ")[0].toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-sun text-navy" : "text-white/75 hover:bg-white/10"}`}>
              <Icon className="w-4 h-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <Link to="/" className="flex items-center gap-2 mt-3 lg:mt-10 text-sm text-white/60 hover:text-white" data-testid="admin-back-to-store"><ArrowLeft className="w-4 h-4" /> Back to store</Link>
      </aside>
      <main className="p-4 sm:p-8 lg:p-10 min-w-0">
        <Routes>
          <Route index element={<AdminOverview />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="destinations" element={<AdminDestinations />} />
          <Route path="discounts" element={<AdminDiscounts />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="emails" element={<AdminEmails />} />
          <Route path="printify" element={<AdminPrintify />} />
        </Routes>
      </main>
    </div>
  );
}
